/**
 * Page-scoped selection helpers for the admin data tables (Batch
 * Center members table, AdminDashboard applicants table/list).
 *
 * Owner rule: a table's header "select all" checkbox applies ONLY to
 * the rows on the current page — never to the whole filtered dataset.
 * Selections are an explicit set of record ids the administrator built
 * up: the header toggle adds the current page's ids (or removes
 * exactly those ids when the page is fully selected), individual row
 * checkboxes toggle single ids, and navigating, filtering, sorting or
 * changing page size never silently selects or deselects anything.
 * Bulk actions always consume the explicit id set, so the selected
 * count is the set's size — duplicates can never inflate it.
 */

export type PageSelectionShare = 'none' | 'some' | 'all';

/** How much of the current page is inside the selection set. */
export function pageSelectionShare(pageIds: string[], selectedIds: string[]): PageSelectionShare {
  if (pageIds.length === 0) return 'none';
  const selected = new Set(selectedIds);
  let count = 0;
  for (const id of pageIds) {
    if (selected.has(id)) count++;
  }
  if (count === 0) return 'none';
  return count === pageIds.length ? 'all' : 'some';
}

/**
 * The header-checkbox toggle for one page:
 * - page fully selected → remove EXACTLY the page's ids (selections
 *   made on other pages, or under other filters, are preserved);
 * - otherwise → union-add the page's ids (order-preserving, deduped),
 *   keeping every id already selected.
 */
export function togglePageSelection(pageIds: string[], selectedIds: string[]): string[] {
  if (pageSelectionShare(pageIds, selectedIds) === 'all') {
    const page = new Set(pageIds);
    return selectedIds.filter((id) => !page.has(id));
  }
  const selected = new Set(selectedIds);
  const next = [...selectedIds];
  for (const id of pageIds) {
    if (!selected.has(id)) {
      selected.add(id);
      next.push(id);
    }
  }
  return next;
}
